import React, { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom';

import '../styles/products/Products.css';


import ScrollableForm from '../components/foundation/ScrollableForm';
import CenteredContentPage from '../components/foundation/CenteredContentPage';

import ProductsEcosystem from '../components/products/ProductsEcosystem';
import BoatHouseRenter from '../components/products/BoatHouseRenter';
import BoatHouseRentee from '../components/products/BoatHouseRentee';
import NicheWallet from '../components/products/NicheWallet';

import Footer from '../components/navigation/Footer';
import FullContentPage from '../components/foundation/FullContentPage';
import ContactUsCard from '../components/design/ContactUsCard';

import ContentTitle from "../components/text/ContentTitle";

const Products = () => {

    // The scroll container (ScrollableForm child) is the scroll root used as
    // the IntersectionObserver root for the spotlight pop-out effect.
    const scrollRef = useRef(null);

    const pageRef = useRef(null);

    const location = useLocation();

    // ── Deep-link: scroll to a specific product when the URL has a hash ──────
    // e.g. navigating to "/products#boathouse-renter-app" brings the user
    // straight to that product rather than the top of the page.
    //
    // On a cold cache, the product/ecosystem images are still loading when
    // this effect first runs — scrolling immediately measures the target's
    // position before those images have taken up their space, so once they
    // load in (growing the page) the target ends up further down than where
    // we scrolled it to. Waiting for every image to finish loading (or
    // erroring) before measuring avoids that; a warm cache resolves this
    // near-instantly since every image is already `complete`.
    useEffect(() => {
        if (!location.hash) return;
        const id = location.hash.slice(1);

        let finished = false;
        let fallbackTimer;

        const scrollToTarget = () => {
            if (finished) return;
            finished = true;
            clearTimeout(fallbackTimer);
            const target = document.getElementById(id);
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        };

        const images = Array.from(document.querySelectorAll('img'));
        const pending = images.filter((img) => !img.complete);

        const onSettle = () => {
            remaining -= 1;
            if (remaining <= 0) {
                // Give layout one more tick to settle after the image paints.
                setTimeout(scrollToTarget, 50);
            }
        };
        let remaining = pending.length;

        if (remaining === 0) {
            const timer = setTimeout(scrollToTarget, 100);
            fallbackTimer = timer;
        } else {
            pending.forEach((img) => {
                img.addEventListener('load', onSettle, { once: true });
                img.addEventListener('error', onSettle, { once: true });
            });
            // Safety net in case an image never fires either event.
            fallbackTimer = setTimeout(scrollToTarget, 1500);
        }

        return () => {
            finished = true;
            clearTimeout(fallbackTimer);
            pending.forEach((img) => {
                img.removeEventListener('load', onSettle);
                img.removeEventListener('error', onSettle);
            });
        };
    }, [location]);


    // Cards that participate in the scroll experience: the ecosystem intro
    // island and each product showcase.
    const SECTION_SELECTOR = '.product-island-container, .product-showcase-container';

    // ── Spotlight: emphasise whichever card is centered ──────────────────────
    useEffect(() => {
        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReduced) return;

        const root = scrollRef.current;
        const page = pageRef.current;
        if (!root || !page) return;
        const sections = Array.from(page.querySelectorAll(SECTION_SELECTOR));
        if (sections.length === 0) return;


        // A narrow central band: a section is "spotlit" while its center crosses
        // the middle of the scroll area, so each card pops as it pushes into focus.
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    entry.target.classList.toggle('is-spotlight', entry.isIntersecting);
                });
            },
            { root, rootMargin: '-42% 0px -42% 0px', threshold: 0 }
        );

        sections.forEach((section) => observer.observe(section));
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <ScrollableForm scrollRef={scrollRef}>
                <ProductsEcosystem />

                <CenteredContentPage ref={pageRef}>

                    <ContentTitle title='Our Custom Software Products' 
                    subTitle='Powerful, easy-to-use solutions built for real-world operations'/>

                    <div className='product-list-container'>
                        <BoatHouseRenter />
                        <BoatHouseRentee />
                        <NicheWallet />
                    </div>
                    <ContactUsCard/>
                </CenteredContentPage>

                <Footer/>
            </ScrollableForm>
        </>
    )

}


export default Products;
